import React, { useContext, useState } from 'react'
import { SiteModeContext } from '../../../components/mainFrame/MainFrame'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import PersonIcon from '@mui/icons-material/Person'
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import SvgIcon from '@mui/material/SvgIcon'
import EmergencyShareIcon from '@mui/icons-material/EmergencyShare'
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'
import VisibilityIcon from '@mui/icons-material/Visibility'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import VaccinesIcon from '@mui/icons-material/Vaccines'
import NextWeekIcon from '@mui/icons-material/NextWeek'
import ReplyIcon from '@mui/icons-material/Reply'
import FindInPageIcon from '@mui/icons-material/FindInPage'
import ShareIcon from '@mui/icons-material/Share'
import QuizIcon from '@mui/icons-material/Quiz'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'
import { useNavigate } from 'react-router-dom'
import { EPCRContext } from './EPCR'
import DeleteIcon from '@mui/icons-material/Delete'
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle'
import DeletePopup from './DeletePopup'
import {
  AttachFile,
  CloudDone,
  CloudDownload,
  CloudOff,
  CloudSync,
  CloudUpload,
} from '@mui/icons-material'
import { UserContext } from '../../../App'
import {
  Alert,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { syncStatusColors } from './EPCRHeader'
import BoltIcon from '@mui/icons-material/Bolt'
import FavoriteIcon from '@mui/icons-material/Favorite'
import Badge from '@mui/material/Badge'
import { listPendingDetails } from './liveCapture/engine'

const SidebarEPCR = ({ selectedEPCRModule, setSelectedEPCRModule, onExit }) => {
  const navigate = useNavigate()
  const { sidebarState, setSidebarState, setSiteMode, setEpcrPracticeMode } =
    useContext(SiteModeContext)
  const {
    setOpenDelete,
    formValues,
    isDraftDirty,
    isSaving,
    uploadState,
    downloadState,
    setFormTypeDialogOpen,
  } = useContext(EPCRContext)
  const { mobileOpen, collapse } = sidebarState
  const { user } = useContext(UserContext)
  const [showDirtyDraftWarning, setShowDirtyDraftWarning] = useState(false)
  const theme = useTheme()
  const showMobileSidebar = useMediaQuery(theme.breakpoints.down('lg'))

  const isUploading = uploadState === 'pending'
  const isDownloading = downloadState === 'pending'
  const hasSyncError = uploadState === 'error' || downloadState === 'error'
  const isFullySynced = uploadState === 'done' && downloadState === 'done'
  const hasTreatmentLocationOwner =
    !!formValues?.review?.currentTreatmentLocation

  const deleteIsAllowed =
    !formValues?.demographics?.firstName &&
    !formValues?.demographics?.lastName &&
    !formValues?.demographics?.lastNameUnknown &&
    !formValues?.demographics?.dob &&
    !formValues?.demographics?.dobUnknown &&
    // Unidentified HEMS patients still have a clinical record if anything
    // was logged on a live console
    !formValues?.cardiacArrest?.events?.length &&
    !formValues?.criticalCare?.events?.length

  const selectModule = (title) => {
    setSelectedEPCRModule(title)
    setSidebarState((prev) => ({ ...prev, mobileOpen: false }))
  }

  const exitRecord = () => {
    if (isDraftDirty || isSaving) {
      setShowDirtyDraftWarning(true)
    } else if (onExit) {
      setSelectedEPCRModule('')
      onExit()
    } else {
      setSiteMode('normal')
      setEpcrPracticeMode(false)
      setSelectedEPCRModule('')
      navigate('/')
    }
  }

  const isCriticalCare = formValues?.mode === 'Critical Care'
  const hasArrest = !!formValues?.cardiacArrest?.startedAt
  const pendingCount =
    isCriticalCare || hasArrest ? listPendingDetails(formValues).length : 0

  const PanelButton = ({ icon, title, label, badge, color }) => {
    const isSelected = title === selectedEPCRModule
    return (
      <Box
        display="flex"
        flexDirection="column"
        // gap={1}
        //   borderRadius={4}
        color="white"
        bgcolor={isSelected && 'contrast.main'}
        alignItems="center"
        justifyContent="center"
        sx={{ cursor: 'pointer' }}
        padding={1}
        onMouseDownCapture={(event) => {
          event.preventDefault()
          event.stopPropagation()
          selectModule(title)
        }}
        onClick={() => selectModule(title)}
      >
        <Badge badgeContent={badge} color="warning" invisible={!badge}>
          <SvgIcon component={icon} sx={color ? { color } : undefined} />
        </Badge>
        <Typography flexWrap="wrap" textAlign="center" color="inherit">
          {label ?? title}
        </Typography>
      </Box>
    )
  }

  const SideBarComponent = () => (
    <Box
      sx={{
        width: '100%',
        backgroundColor: 'primary.main',
        zIndex: 1,
      }}
      flexDirection="column"
      alignItems="center"
      gap={2}
      height="100%"
      display="flex"
      maxHeight="100vh"
      // overflow='scroll'
    >
      <Button
        sx={{ color: 'white' }}
        startIcon={<ReplyIcon />}
        onMouseDownCapture={(event) => {
          event.preventDefault()
          event.stopPropagation()
          exitRecord()
        }}
        onClick={exitRecord}
        size="large"
        fullWidth
      >
        Exit
      </Button>
      <Button
        sx={{ color: 'white', marginTop: -1.5 }}
        startIcon={<ChangeCircleIcon />}
        onClick={() => setFormTypeDialogOpen(true)}
        size="small"
        fullWidth
      >
        Record Type
      </Button>
      {formValues?.epcrNumber && (
        <Box
          // display="flex"
          color="#fff"
          bgcolor="secondary.main"
          alignItems="center"
          justifyContent="center"
          sx={{ width: '100%', py: 0.5 }}
        >
          <Typography
            flexWrap="wrap"
            textAlign="center"
            color="secondary.contrastText"
          >
            {formValues.epcrNumber}
          </Typography>
        </Box>
      )}
      <Stack width="100%" spacing={1} minHeight={0} overflow="auto">
        {isCriticalCare && (
          <PanelButton
            icon={BoltIcon}
            title="Live"
            label="Live console"
            badge={pendingCount}
          />
        )}
        {hasArrest && (
          <PanelButton
            icon={FavoriteIcon}
            title="Arrest"
            label="Cardiac arrest"
            badge={isCriticalCare ? 0 : pendingCount}
            color="#FF8A80"
          />
        )}
        <PanelButton icon={EmergencyShareIcon} title="Incident" />
        <PanelButton icon={PersonIcon} title="Demographics" />
        <PanelButton icon={MedicalServicesIcon} title="PMHx" />
        <PanelButton icon={QuizIcon} title="History" />
        <PanelButton icon={VisibilityIcon} title="Assess" />
        <PanelButton icon={MonitorHeartIcon} title="Vitals" />
        <PanelButton icon={VaccinesIcon} title="Treatment" />
        <PanelButton icon={AttachFile} title="Attachments" />
        <PanelButton icon={NextWeekIcon} title="Plan" />
        {/* <PanelButton icon={AmbulanceIcon} title="Transport" /> */}
        <PanelButton icon={ShareIcon} title="Sharing" />
        {(formValues?.review?.completedBy?._id === user?._id ||
          hasTreatmentLocationOwner) &&
          deleteIsAllowed && (
            <Button
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => setOpenDelete(true)}
              sx={{ color: 'white' }}
            >
              Delete
            </Button>
          )}
        <PanelButton icon={FindInPageIcon} title="Review" />
      </Stack>
    </Box>
  )
  const toggleDrawer = (event) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return
    }
    setSidebarState((prev) => ({ ...prev, mobileOpen: false }))
  }

  return (
    <>
      <Box
        sx={{
          display: {
            xs: 'none',
            lg: 'flex',
          },
          width: '140px',
        }}
      >
        {SideBarComponent()}
      </Box>
      {showMobileSidebar && (
        <Box
          sx={{
            display: {
              xs: 'flex',
              lg: 'none',
            },
          }}
        >
          <Drawer
            anchor="left"
            open={mobileOpen}
            onClose={toggleDrawer}
            sx={{
              display: {
                xs: 'flex',
                lg: 'none',
              },
            }}
          >
            {SideBarComponent()}
          </Drawer>
        </Box>
      )}
      <Dialog
        open={showDirtyDraftWarning}
        onClose={() => {
          if (isFullySynced) setShowDirtyDraftWarning(false)
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, overflow: 'hidden' },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {isFullySynced ? (
              <CloudDone color="success" />
            ) : hasSyncError ? (
              <CloudOff color="error" />
            ) : (
              <CloudSync color="warning" />
            )}

            <Typography variant="h6" fontWeight={700}>
              {isFullySynced
                ? 'ePCR Synced'
                : hasSyncError
                  ? 'Sync Problem'
                  : 'Syncing ePCR'}
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2}>
            {!isFullySynced && !hasSyncError && <LinearProgress />}

            <Alert
              severity={
                isFullySynced ? 'success' : hasSyncError ? 'error' : 'warning'
              }
              sx={{ borderRadius: 3 }}
            >
              {isFullySynced
                ? 'This ePCR has been synced successfully. You can safely continue.'
                : hasSyncError
                  ? 'This ePCR could not be synced. Please check your connection before leaving.'
                  : 'This ePCR is still syncing. Please wait before leaving this page.'}
            </Alert>
            <Stack direction="row" gap={1} justifyContent="center">
              <CloudUpload
                sx={{
                  color: () => syncStatusColors(uploadState),
                  width: '4rem',
                  height: '4rem',
                }}
              />
              <CloudDownload
                sx={{
                  color: () => syncStatusColors(downloadState),
                  width: '4rem',
                  height: '4rem',
                }}
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          {!isFullySynced && (
            <Button
              onClick={() => setShowDirtyDraftWarning(false)}
              variant="outlined"
            >
              Stay on ePCR
            </Button>
          )}

          {isFullySynced && (
            <Button
              onClick={() => {
                setShowDirtyDraftWarning(false)
                setSelectedEPCRModule('')
                if (onExit) {
                  onExit()
                } else {
                  setSiteMode('normal')
                  setEpcrPracticeMode(false)
                  navigate('/')
                }
              }}
              variant="contained"
              color="success"
            >
              Continue
            </Button>
          )}

          {hasSyncError && (
            <Button
              color="warning"
              variant="contained"
              onClick={() => {
                // allow leaving but keep local draft
              }}
            >
              Leave & Keep Draft
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default SidebarEPCR
